<template>
  <div class="sf-gallery">
    <div class="sf-gallery__stage">
      <div ref="glide" class="glide">
        <div class="glide__track" data-glide-el="track">
          <ul class="glide__slides">
            <li
              v-for="(picture, index) in images"
              :key="'slide-' + index"
              class="glide__slide"
            >
              <video
                v-if="picture.videoUrl"
                class="sf-gallery__big-video"
                :src="picture.videoUrl"
                playsinline
                muted
                autoplay
                preload="metadata"
                controls
                @click="$emit('click:stage', { picture, index })"
              />
              <SfImage
                v-else
                class="sf-gallery__big-image"
                :src="picture.desktop.url"
                :srcset="picture.srcset || undefined"
                :alt="picture.alt"
                :width="imageWidth"
                :height="imageHeight"
                @click="$emit('click:stage', { picture, index })"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="sf-gallery__thumbs">
      <slot name="thumbs" v-bind="{ images, active: activeIndex, go }">
        <SfButton
          v-for="(image, index) in images"
          :key="'img-' + index"
          class="sf-button--pure sf-gallery__item"
          :class="{ 'sf-gallery__item--selected': index === activeIndex }"
          @click="go(index)"
        >
          <template v-if="!image.videoUrl">
            <SfImage
              class="sf-gallery__thumb"
              :src="image.mobile.url"
              :alt="image.alt"
              :width="thumbWidth"
              :height="thumbHeight"
            />
          </template>
          <div v-else class="sf-gallery__video-thumb" :style="videoThumbStyle">
            <div class="sf-gallery__video-thumb-play" />
          </div>
        </SfButton>
      </slot>
    </div>
  </div>
</template>
<script>
import Glide from '@glidejs/glide';
import SfImage from '@storefront-ui/vue/src/components/atoms/SfImage/SfImage.vue';
import SfButton from '@storefront-ui/vue/src/components/atoms/SfButton/SfButton.vue';

export default {
  name: 'MModalSfGallery',
  components: {
    SfImage,
    SfButton
  },
  props: {
    images: {
      type: Array,
      default: () => []
    },
    imageWidth: {
      type: [Number, String],
      default: 2000
    },
    imageHeight: {
      type: [Number, String],
      default: 2000
    },
    thumbWidth: {
      type: [Number, String],
      default: 160
    },
    thumbHeight: {
      type: [Number, String],
      default: 160
    },
    current: {
      type: Number,
      default: 1
    },
    sliderOptions: {
      type: Object,
      default () {
        return {
          type: 'slider',
          autoplay: false,
          rewind: false,
          gap: 0
        };
      }
    }
  },
  data () {
    return {
      glide: null,
      activeIndex: this.current - 1
    };
  },
  computed: {
    firstProductImageUrl () {
      const firstImage = this.images.find((image) => image && !image.videoUrl && image.mobile && image.mobile.url);
      return firstImage ? firstImage.mobile.url : '';
    },
    videoThumbStyle () {
      if (!this.firstProductImageUrl) return {};
      return {
        backgroundImage: `url(${this.firstProductImageUrl})`
      };
    },
    updatedSliderOptions () {
      return { ...this.sliderOptions, startAt: this.activeIndex };
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.images.length < 1) return;
      const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
      glide.on('run', () => {
        this.go(glide.index);
      });
      glide.mount();
      this.glide = glide;
    });
  },
  beforeDestroy () {
    if (this.glide) {
      this.glide.destroy();
    }
  },
  methods: {
    go (index) {
      if (!this.glide) return;
      this.activeIndex = index;
      this.$emit('click', index + 1);
      this.glide.go(`=${index}`);
    }
  }
};
</script>
<style lang="scss">
@import "~@storefront-ui/shared/styles/components/molecules/SfGallery.scss";

.sf-gallery__big-video {
  display: block;
  max-width: 95%;
  max-height: 95%;
  object-fit: cover;
  object-position: center;
  background: transparent;
  margin: auto;
  align-self: center;
}

.sf-gallery__video-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: inherit;
  overflow: hidden;
}

.sf-gallery__video-thumb-play {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 2px solid #fff;
  border-radius: 50%;
}

.sf-gallery__video-thumb-play::before {
  content: "";
  width: 0;
  height: 0;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 14px solid #fff;
  margin-left: 3px;
}
</style>
